import { FormProvider, getFormProps } from '@conform-to/react'
import {
	Badge,
	Box,
	Flex,
	IconButton,
	Table,
	Tooltip,
	VisuallyHidden,
} from '@radix-ui/themes'
import { Link } from '@remix-run/react'
import { cartListItemTestIds } from 'app/routes/_layout'
import * as React from 'react'
import { RxCross1, RxMinus, RxPlus } from 'react-icons/rx'

import { FormDefaults, FormDefaultsHiddenField } from '~/components/common/form-defaults.tsx'
import { ProductName } from '~/components/common/product-name'
import { Image, IMAGE_FILTERS } from '~/components/ui/image.tsx'
import { Price } from '~/components/ui/price.tsx'
import { TextField } from '~/components/ui/text-field.tsx'
import { type GetProductOption } from '~/models/ecommerce-providers/sylius/shop/fetch/product-option/get-product-option.ts'
import { type ProductImageNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-image.ts'
import { type ProductOptionValueNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option-value.ts'
import { type ProductOptionNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option.ts'
import { type Locale } from '~/models/locale.ts'
import {
	CART_FORM_REMOVE_ITEM,
	CART_FORM_SET_QUANTITY,
	CART_INPUTS,
} from '~/routes/($lang).cart/cart.component.tsx'
import { type App } from '~/routes/_layout/root.component.tsx'
import { useCartItem } from '~/utils/hooks/use-cart-item.ts'

/* -------------------------------------------------------------------------------------------------
 * CartViewItem
 * -----------------------------------------------------------------------------------------------*/

type CartItemProps = {
	image: ProductImageNormalized
	locale: Locale
	mutate: boolean
	name: string
	productSlug: string
	optionValues: ProductOptionValueNormalized[] | undefined
	orderItemId: string
	variantId: string
	unitPrice: number
	quantity: number
	setApp: React.Dispatch<React.SetStateAction<App>>
	isOptimistic?: boolean
}

const CartViewItem: React.FC<CartItemProps> = (props) => {
	const {
		locale,
		mutate,
		optionValues,
		unitPrice,
		image,
		quantity,
		name,
		productSlug,
		orderItemId,
		variantId,
		setApp,
		isOptimistic = true,
	} = props

	const {
		optimisticQuantity,
		optimisticDeleting,
		optimisticPrice,
		setQuantityFetcher,
		removeItemFetcher,
		setQuantityFormValues,
		setQuantityForm,
		setQuantityFields,
		increaseQuantityFormValues,
		increaseQuantityForm,
		increaseQuantityFields,
		decreaseQuantityFormValues,
		decreaseQuantityForm,
		decreaseQuantityFields,
		removeItemFormValues,
		removeItemForm,
		removeItemFields,
		t,
		location,
		isChanged,
		setIsChanged,
	} = useCartItem({
		setApp,
		name,
		orderItemId,
		quantity,
		unitPrice,
		variantId,
		isOptimistic,
		slug: productSlug,
	})

	return (
		<Table.Row
			align="center"
			hidden={optimisticDeleting}
			className="app-ImageShadowContainer"
		>
			<Table.Cell>
				<Box position="relative" className="block aspect-square w-40">
					<Link to={`/${locale}/products/${productSlug}`} prefetch="viewport">
						<Image
							alt={name}
							className="app-imageShadow absolute inset-0"
							filter={IMAGE_FILTERS.gridFilter}
							src={image.path!}
						/>
					</Link>
				</Box>
			</Table.Cell>
			<Table.Cell>
				<Link to={`/${locale}/products/${productSlug}`} prefetch="viewport">
					<ProductName
						name={name}
						size={{
							initial: '3',
							xs: '4',
							sm: '5',
						}}
					/>
				</Link>
				<ul>
					{optionValues?.map(({ code, value, option }) => (
						<li key={code}>
							<Tooltip content={(option as ProductOptionNormalized).name!}>
								<Badge
									color="gray"
									className="mt-1 inline-flex items-center justify-center gap-x-1"
								>
									{(option as GetProductOption).name!.replace(
										'|',
										`: ${value}`,
									)}
								</Badge>
							</Tooltip>
						</li>
					))}
				</ul>
			</Table.Cell>
			<Table.Cell>
				<Flex display="inline-flex">
					<Tooltip content={t('label.unitPrice')}>
						<Price locale={locale} color="gray">
							{optimisticPrice}
						</Price>
					</Tooltip>
				</Flex>
			</Table.Cell>
			<Table.Cell>
				<Flex display="inline-flex" align="center">
					<FormProvider context={setQuantityForm.context}>
						<setQuantityFetcher.Form
							method="post"
							action={`/${locale}${CART_FORM_SET_QUANTITY.action}${location.search}`}
							{...getFormProps(setQuantityForm)}
							preventScrollReset
							className="mx-3 grow"
							onChange={() => setIsChanged(true)}
						>
							<FormDefaults
                form={setQuantityForm}
                fields={setQuantityFields}
                values={setQuantityFormValues}
              />
              <FormDefaultsHiddenField meta={setQuantityFields[CART_INPUTS.orderItemId]} />
							<TextField
								disabled={!mutate}
								meta={setQuantityFields[CART_INPUTS.quantity]}
								t={t}
								type="number"
								onBlur={(e) => {
									if (isChanged) {
										setQuantityFetcher.submit(e.currentTarget.form)
									}
								}}
							>
								{t('label.quantity')}
							</TextField>
						</setQuantityFetcher.Form>
					</FormProvider>
				</Flex>
			</Table.Cell>
			<Table.Cell>
				<Flex display="inline-flex">
					<Tooltip content={t('label.total')}>
						<Price locale={locale} color="gray">
							{unitPrice * optimisticQuantity}
						</Price>
					</Tooltip>
				</Flex>
			</Table.Cell>
			{mutate && (
				<Table.Cell>
					<Flex display="inline-flex" align="center" gapX="2">
						<FormProvider context={removeItemForm.context}>
							<Tooltip content={t('items.common.removeCartItem')}>
								<removeItemFetcher.Form
									method="post"
									action={`/${locale}${CART_FORM_REMOVE_ITEM.action}${location.search}`}
									{...getFormProps(removeItemForm)}
									preventScrollReset
								>
									<FormDefaults
										form={removeItemForm}
										fields={removeItemFields}
                    values={removeItemFormValues}
									/>
                  <FormDefaultsHiddenField meta={removeItemFields[CART_INPUTS.itemId]} />
									<IconButton
										variant="soft"
										radius="full"
										color="red"
										data-testid={cartListItemTestIds.removeFromCart}
										type="submit"
									>
										<VisuallyHidden>
											{t('items.button.removeCartItemSubmit')}
										</VisuallyHidden>
										<RxCross1 />
									</IconButton>
								</removeItemFetcher.Form>
							</Tooltip>
						</FormProvider>
						<FormProvider context={decreaseQuantityForm.context}>
							<Tooltip content={t('items.common.decrementCartItemQuantity')}>
								<setQuantityFetcher.Form
									method="post"
									action={`/${locale}${CART_FORM_SET_QUANTITY.action}${location.search}`}
									{...getFormProps(decreaseQuantityForm)}
									preventScrollReset
								>
									<FormDefaults
										form={decreaseQuantityForm}
										fields={decreaseQuantityFields}
                    values={decreaseQuantityFormValues}
									/>
                  <FormDefaultsHiddenField meta={decreaseQuantityFields[CART_INPUTS.orderItemId]} />
                  <FormDefaultsHiddenField
                    meta={decreaseQuantityFields[CART_INPUTS.quantity]}
                    disabled={optimisticQuantity - 1 <= 0}
                  />
									<IconButton
										variant="soft"
										radius="full"
										data-testid="decrement-cart"
										type="submit"
										disabled={optimisticQuantity - 1 <= 0}
									>
										<VisuallyHidden>
											{t('items.button.decrementCartItemQuantitySubmit')}
										</VisuallyHidden>
										<RxMinus />
									</IconButton>
								</setQuantityFetcher.Form>
							</Tooltip>
						</FormProvider>
						<FormProvider context={increaseQuantityForm.context}>
							<Tooltip content={t('items.common.incrementCartItemQuantity')}>
								<setQuantityFetcher.Form
									method="post"
									action={`/${locale}${CART_FORM_SET_QUANTITY.action}${location.search}`}
									{...getFormProps(increaseQuantityForm)}
									preventScrollReset
								>
									<FormDefaults
										form={increaseQuantityForm}
										fields={increaseQuantityFields}
                    values={increaseQuantityFormValues}
									/>
                  <FormDefaultsHiddenField meta={increaseQuantityFields[CART_INPUTS.orderItemId]} />
                  <FormDefaultsHiddenField meta={increaseQuantityFields[CART_INPUTS.quantity]} />
									<IconButton
										variant="soft"
										radius="full"
										data-testid={cartListItemTestIds.incrementCart}
										type="submit"
									>
										<VisuallyHidden>
											{t('items.button.incrementCartItemQuantitySubmit')}
										</VisuallyHidden>
										<RxPlus />
									</IconButton>
								</setQuantityFetcher.Form>
							</Tooltip>
						</FormProvider>
					</Flex>
				</Table.Cell>
			)}
		</Table.Row>
	)
}
CartViewItem.displayName = 'Item'

/* -----------------------------------------------------------------------------------------------*/

export { CartViewItem }
